import { Box, Button, HStack, VStack } from "@chakra-ui/react"
import { Text } from "@chakra-ui/react"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"

interface FigureInternalProps {
  children: React.ReactNode
  fontSize?: string | Record<string, string>
  truncate?: boolean
  maxWidth?: string
  tooltip?: React.ReactNode
  tooltipDisabled?: boolean
  color?: string
  fontWeight?: string
  compact?: boolean
  onClick?: () => void
  link?: React.ReactNode
  whiteSpace?: any
  wordBreak?: any
  lineHeight?: any
  justify?: any
  flex?: any
  height?: any
  [key: string]: any
}

const FigureTitle = ({
  children,
  fontSize = "13px",
}: FigureInternalProps) => {
  return (
    <Text fontSize={fontSize} fontWeight="600" color="#2B2B2B">
      {children}
    </Text>
  )
}

const FigureLabel = ({
  children,
  fontSize = "13px",
  tooltip,
  tooltipDisabled,
  compact: _compact,
  ...props
}: FigureInternalProps) => {
  const textElement = (
    <Text fontSize={fontSize} color="#545454" {...props}>
      {children}
    </Text>
  )

  if (tooltip && !tooltipDisabled) {
    return <Tooltip content={tooltip}>{textElement}</Tooltip>
  }

  return textElement
}

const FigureSubText = ({
  children,
  fontSize = "13px",
  tooltip,
  tooltipDisabled,
  compact: _compact,
  link,
  ...props
}: FigureInternalProps) => {
  const textElement = (
    <HStack width="100%" justifyContent="flex-start" gap={2}>
      <Text fontSize={fontSize} fontWeight="500" color="#545454" {...props}>
        {children}
      </Text>
      {link}
    </HStack>
  )
  return textElement
}

const FigureBox = ({
  children,
  fontSize = "13px",
}: FigureInternalProps) => {
  return (
    <Box fontSize={fontSize} width="100%">
      {children}
    </Box>
  )
}

const FigureValue = ({
  children,
  fontSize = { base: "26px", "2xl": "28px" },
  truncate,
  maxWidth,
  tooltip,
  tooltipDisabled,
  compact: _compact,
  onClick,
  whiteSpace,
  wordBreak,
  lineHeight,
  ...props
}: FigureInternalProps) => {
  const textElement = (
    <Text
      fontSize={fontSize}
      fontWeight="700"
      letterSpacing="-0.015em"
      lineHeight={lineHeight ?? "1.1"}
      color="#2B2B2B"
      truncate={truncate}
      maxWidth={maxWidth}
      onClick={onClick}
      whiteSpace={whiteSpace}
      wordBreak={wordBreak}
      {...props}
    >
      {children}
    </Text>
  )

  if (tooltip && !tooltipDisabled) {
    return <Tooltip content={tooltip}>{textElement}</Tooltip>
  }

  return textElement
}

const FigureRoot = ({ children, compact: _compact, justify, justifyContent, flex, height, ...props }: FigureInternalProps) => {
  return (
    <VStack
      gap={1.5}
      alignItems="flex-start"
      width="100%"
      justify={justify}
      justifyContent={justifyContent ?? justify}
      flex={flex}
      height={height}
      {...props}
    >
      {children}
    </VStack>
  )
}

const FigureAction = ({ children, onClick, disabled, mt = 3, ...props }: FigureInternalProps & { disabled?: boolean }) => {
  return (
    <Button mt={mt} variant="hydrologiq_primary" onClick={onClick} disabled={disabled} {...props}>
      {children}
    </Button>
  )
}

const FigureContent = {
  Title: FigureTitle,
  Label: FigureLabel,
  SubText: FigureSubText,
  Value: FigureValue,
  Root: FigureRoot,
  Box: FigureBox,
  Action: FigureAction,
}

export default FigureContent
